/**
 * useEmployeeAssignmentMutations.ts
 * Optimistic commit funnel for Employee Schedule edits (UX overhaul).
 * Each mutation: snapshot → optimistic local patch → persist → quiet reload on
 * success / restore on error. Constraint 409s (SkillGateError /
 * AbsenceDayConflictError) are re-thrown so the page can open the mentor
 * dialog / show the hard-block message (Q3B — no retry for absence conflicts).
 *
 * Wave 3 (percent retirement): commitPercent is GONE — the drawer's
 * "Allocation %" field was removed; the server pins Percent=100.
 */

import { useCallback, useState } from 'react';
import {
  reassignAssignment, spanAssignment, type EmployeeBar,
} from '../services/scheduleEmployeeBars';
import { deleteEmployeeAssignment } from '../services/scheduleAssignments';
import type { UseEmployeeScheduleDataResult } from './useEmployeeScheduleData';

export interface ReassignOpts {
  mentorUserKey?: string | null;
  /** W2-8 (P-15): "Assign anyway" — bypass the advisory per-week mentor gate. */
  confirmSkillGate?: boolean;
}

export interface EmployeeMutations {
  busy: boolean;
  commitDelete: (bar: EmployeeBar) => Promise<void>;
  commitReassign: (bar: EmployeeBar, targetUserKey: string, opts?: ReassignOpts) => Promise<void>;
  commitSpan: (bar: EmployeeBar, startWeek: string, endWeek: string) => Promise<void>;
}

export function useEmployeeAssignmentMutations(
  data: UseEmployeeScheduleDataResult,
  notify: (msg: string, severity?: 'success' | 'error' | 'info') => void,
): EmployeeMutations {
  const [busy, setBusy] = useState(false);
  const { snapshot, restore, removeBars, updateBar, reload } = data;

  const commitDelete = useCallback(async (bar: EmployeeBar) => {
    const snap = snapshot();
    removeBars([bar.assignmentKey]);
    setBusy(true);
    try {
      await deleteEmployeeAssignment(bar.assignmentKey);
      notify('Assignment removed', 'success');
      void reload({ quiet: true });
    } catch (err) {
      restore(snap);
      notify(err instanceof Error ? err.message : 'Delete failed', 'error');
    } finally { setBusy(false); }
  }, [snapshot, restore, removeBars, reload, notify]);

  const commitReassign = useCallback(async (bar: EmployeeBar, targetUserKey: string, opts?: ReassignOpts) => {
    if (targetUserKey === bar.userKey) return;
    const snap = snapshot();
    updateBar(bar.assignmentKey, { userKey: targetUserKey }); // optimistic move
    setBusy(true);
    try {
      await reassignAssignment(bar.assignmentKey, { newUserKey: targetUserKey, ...opts });
      notify('Reassigned', 'success');
      void reload({ quiet: true });
    } catch (err) {
      restore(snap); // roll back before the page shows a mentor/overbook dialog
      throw err;
    } finally { setBusy(false); }
  }, [snapshot, restore, updateBar, reload, notify]);

  const commitSpan = useCallback(async (bar: EmployeeBar, startWeek: string, endWeek: string) => {
    setBusy(true);
    try {
      await spanAssignment(bar.assignmentKey, { startWeek, endWeek });
      notify('Weeks updated', 'success');
      void reload({ quiet: true });
    } catch (err) {
      // Absence-day conflicts land here too — the server message is verbatim
      // and final (Q3B: no confirm/bypass path exists).
      notify(err instanceof Error ? err.message : 'Span failed', 'error');
      throw err;
    } finally { setBusy(false); }
  }, [reload, notify]);

  return { busy, commitDelete, commitReassign, commitSpan };
}
